import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Input, Select, Calendar, Button, UploadButton } from '@baseV2'
import { TOAST_TYPE } from '@base'
import { MBOOK_ORGANISATION_ID, strings } from '@constants'
import { getAllAccountDetails, getAllClientsApi, getOrganisation, getDueInvoices, createInvoicePayment } from '@data'
import { toggleDrawer } from '@data/state/action/root'
import { showLoader, showToast } from '@data/state/action'
import { displayAmount, showDateFormat, allowNumericKeyDown } from '@utils'
import { Spacings, Text } from '@styles'
import AttachInvoices from './AttachInvoices'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	font-family: ${({ theme }) => theme.fontFamily.inter};
`
const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B};
	overflow-y: auto;
	flex: 1;
`
const Req = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`
// Label with a red required-asterisk.
const reqLabel = (text) => (
	<>
		{text} <Req>*</Req>
	</>
)

const SectionLabel = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.colors.text.secondary};
	margin-top: ${Spacings.SPACING_3B};
	padding-bottom: ${Spacings.SPACING_2B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	&::before {
		content: '';
		display: inline-block;
		width: 3px;
		height: 0.9rem;
		border-radius: 2px;
		background: ${({ theme }) => theme.colors.brand.primary};
	}
`

const SummaryBar = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: ${Spacings.SPACING_2B};
	margin: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
	background: ${({ theme }) => theme.colors.surface.sunken};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const SummaryItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	padding: 0 ${Spacings.SPACING_2B};
	/* min-width:0 lets a grid item shrink below its content so long amounts wrap instead of
	   overflowing into the next column (which was causing the overlap). */
	min-width: 0;
	border-right: 1px solid ${({ theme }) => theme.colors.border.default};

	&:last-child {
		border-right: none;
	}
`

const SummaryLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const SummaryValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme, $advance }) => ($advance ? theme.colors.brand.primary : theme.colors.text.primary)};
	max-width: 100%;
	text-align: center;
	overflow-wrap: anywhere;
`

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_4B};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`

// Normalise a due-invoice record (getDueInvoices) into the shape the UI needs.
const normalizeInvoice = (inv, idx) => {
	const rawDate = inv.invoice_date ?? inv.due_date ?? inv.dueDate
	let dueDateLabel = '--'
	if (rawDate) {
		const d = new Date(rawDate)
		dueDateLabel = Number.isNaN(d.getTime()) ? String(rawDate) : showDateFormat(d)
	}
	const shipping = inv.shipping_address ?? inv.shipping_location ?? inv.delivery_address ?? inv.location ?? inv.address
	// Show only city, state, pincode (not the full address).
	const shippingLocation =
		typeof shipping === 'object' && shipping !== null
			? [shipping.city, shipping.state, shipping.pincode].filter(Boolean).join(', ')
			: shipping || ''
	return {
		raw: inv,
		id: inv.transaction_no ?? inv.original_invoice_no ?? idx,
		transactionNo: inv.transaction_no ?? '',
		number: inv.original_invoice_no ?? inv.invoice_number ?? '--',
		dueDateLabel,
		invoiceAmount: Number(inv.invoice_amount ?? inv.total_amount ?? 0),
		balanceDue: Number(inv.pending_amount ?? inv.balance_due ?? inv.due_amount ?? 0),
		// Show the Zoho status.
		deliveryStatus: inv.zoho_status ?? inv.delivery_status ?? inv.status ?? '',
		shippingLocation,
		docUrl: inv.document_url ?? inv.doc_url ?? inv.file_path ?? inv.invoice_url ?? inv.invoice_document ?? '',
	}
}

const AddPayment = ({ update, preset }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const activeCommodity = user?.commodity?.find((item) => item?.active)
	// Only the commodities assigned to the logged-in user.
	const commodities = useMemo(() => user?.commodity ?? [], [user?.commodity])
	const commodityId = activeCommodity?.commodity_id

	const [clients, setClients] = useState([])
	const [accounts, setAccounts] = useState([])
	// eslint-disable-next-line no-unused-vars
	const [orgDetails, setOrgDetails] = useState(null)
	const [dueInvoices, setDueInvoices] = useState([])
	const [allocations, setAllocations] = useState({})
	const [allocErrors, setAllocErrors] = useState({})
	const [documents, setDocuments] = useState([])
	const [form, setForm] = useState({
		organisation: null,
		account: null,
		remark: '',
		amount: '',
		paymentDate: new Date().getTime(),
		// Commodity for this payment (defaults to the user's active commodity).
		commodity: activeCommodity ?? null,
	})

	const selectedCommodityId = form.commodity?.commodity_id ?? commodityId
	useEffect(() => {
		getAllClientsApi(user?.user_id, selectedCommodityId)
			.then((data) => {
				const list = (data ?? []).map((item) => ({
					...item,
					display_name: !item.organisation_name || item.organisation_name === '' ? item.organisation_id : item.organisation_name,
				}))
				setClients(list)
				setForm((prev) => {
					if (!prev.organisation) return prev
					const stillThere = list.find((c) => c.organisation_id === prev.organisation.organisation_id)
					if (stillThere) return prev
					setOrgDetails(null)
					return { ...prev, organisation: null, account: null }
				})
			})
			.catch(() => setClients([]))
	}, [user?.user_id, selectedCommodityId])


	useEffect(() => {
		getAllAccountDetails(MBOOK_ORGANISATION_ID, user?.user_id)
			.then((res) => {
				const list = Array.isArray(res?.account_details) ? res.account_details : []
				setAccounts(
					list.map((acc) => ({
						...acc,
						display_label: acc?.name || acc?.bank_name || acc?.account_number || '--',
					}))
				)
			})
			.catch(() => setAccounts([]))
	}, [user?.user_id])

	const invoices = useMemo(() => (dueInvoices ?? []).map(normalizeInvoice), [dueInvoices])

	// Total = entered amount; Attached = Σ allocations; Advance = Total − Attached.
	const total = Number(form.amount) || 0
	const attached = useMemo(() => Object.values(allocations).reduce((sum, v) => sum + (Number(v) || 0), 0), [allocations])
	const advance = Math.max(0, total - attached)

	const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

	const handleAmountChange = (raw) => {
		if (raw === '') return setField('amount', '')
		// strip anything that isn't a digit or dot, then collapse to a single decimal point
		const cleaned = raw.replace(/[^\d.]/g, '')
		const [intPartRaw, ...decParts] = cleaned.split('.')
		const intPart = intPartRaw.slice(0, 12)
		const hasDecimal = decParts.length > 0
		const decPart = hasDecimal ? `.${decParts.join('').slice(0, 2)}` : ''
		return setField('amount', intPart + decPart)
	}

	// On organisation select: reset dependent state, fetch org details + due invoices.
	const onOrganisationChange = (org) => {
		setForm((prev) => ({ ...prev, organisation: org, account: null }))
		setOrgDetails(null)
		setDueInvoices([])
		setAllocations({})
		setAllocErrors({})
		const orgId = org?.organisation_id
		if (orgId) {
			getOrganisation(orgId)
				.then((res) => {
					const details = res?.mbook_bank ? res : res?.data
					setOrgDetails(details)
					const saved = details?.mbook_bank
					const matchedAccount = saved
						? accounts.find((acc) =>
								saved?.account_id ? acc?.account_id === saved?.account_id : acc?.account_number === saved?.account_number
						  )
						: null
					// Only auto-fill if the user hasn't already picked an account (don't overwrite a
					// manual selection when getOrganisation resolves late).
					if (matchedAccount) {
						setForm((prev) => (prev.account ? prev : { ...prev, account: matchedAccount }))
					}
				})
				.catch(() => setOrgDetails(null))
		}

	}

	// Due invoices always use the CURRENTLY selected organisation + commodity. When the commodity
	// change also changed the organisation, this runs with the new organisation's id.
	const selectedOrgId = form.organisation?.organisation_id
	useEffect(() => {
		setAllocations({})
		setAllocErrors({})
		if (!selectedOrgId) {
			setDueInvoices([])
			return
		}
		let cancelled = false
		getDueInvoices(selectedOrgId, selectedCommodityId)
			.then((res) => {
				if (!cancelled) setDueInvoices(res ?? [])
			})
			.catch(() => {
				if (!cancelled) setDueInvoices([])
			})
		// Ignore a slow response that resolves after the selection changed again.
		return () => {
			cancelled = true
		}
	}, [selectedOrgId, selectedCommodityId])

	// When opened from a listing row, pre-fill THIS row's values once the clients load. The payment
	// row has no organisation_id, so match the organisation by id (if any) or by name. Selecting the
	// org fetches its details, due invoices and auto-fills the account (same as a manual pick); then
	// carry over the row's remark and amount. Runs once so it never overrides later manual edits.
	const presetAppliedRef = useRef(false)
	useEffect(() => {
		if (presetAppliedRef.current || !preset || clients.length === 0) return
		const normalize = (v) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
		const matched = clients.find(
			(c) =>
				(preset.organisationId && c?.organisation_id === preset.organisationId) ||
				(preset.organisationName && normalize(c?.organisation_name) === normalize(preset.organisationName))
		)
		if (!matched) return
		presetAppliedRef.current = true
		onOrganisationChange(matched)
		setForm((prev) => ({
			...prev,
			...(preset.remark != null ? { remark: String(preset.remark) } : {}),
			...(preset.amount != null && preset.amount !== '' ? { amount: String(preset.amount) } : {}),
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preset, clients])

	// Allocate an amount to an invoice: clamp to the invoice's balance due AND to the remaining
	// payment budget so attached never exceeds Total and advance auto-reduces (never negative).
	// Uses the functional updater so the running total is read from the latest state, not a stale
	// closure — keeps Attached/Advance correct even while typing quickly across invoices.
	const handleAllocation = (invoice, rawValue) => {
		if (rawValue === '') {
			setAllocations((a) => ({ ...a, [invoice.id]: '' }))
			setAllocErrors((e) => ({ ...e, [invoice.id]: undefined }))
			return
		}
		let val = Number(rawValue)
		if (Number.isNaN(val) || val < 0) val = 0

		// No pending amount on this invoice → don't set any value, just show a message.
		if (invoice.balanceDue <= 0) {
			setAllocErrors((e) => ({ ...e, [invoice.id]: 'No pending amount on this invoice' }))
			setAllocations((a) => ({ ...a, [invoice.id]: '' }))
			return
		}

		setAllocations((a) => {
			// sum of every OTHER invoice's allocation (from the latest state)
			const others = Object.entries(a).reduce((sum, [id, v]) => (id === String(invoice.id) ? sum : sum + (Number(v) || 0)), 0)
			const budgetLeft = Math.max(0, total - others)
			const cap = Math.min(invoice.balanceDue, budgetLeft)

			if (total <= 0) {
				setAllocErrors((e) => ({ ...e, [invoice.id]: 'Enter payment amount first' }))
				return a
			}

			// The whole payment amount is already attached to other invoices — no budget left for
			// this one. Block the input entirely: don't write anything (not even 0) and flag it.
			if (cap <= 0) {
				setAllocErrors((e) => ({ ...e, [invoice.id]: 'Payment amount fully allocated' }))
				return a
			}

			// round to 2 decimals to avoid floating-point artefacts like 95182.79999999981
			const clamped = Math.round(Math.min(val, cap) * 100) / 100
			// The value is auto-clamped to a valid amount (<= balance due AND <= remaining budget),
			// so a clamped value is never an error.
			setAllocErrors((e) => ({ ...e, [invoice.id]: undefined }))

			return { ...a, [invoice.id]: clamped }
		})
	}

	const close = () => dispatch(toggleDrawer(false))

	// Validate required (*) fields; surface via the existing toast.
	const validate = () => {
		if (!form.organisation?.organisation_id) return `${strings('select', 'organisation')} is required`
		if (!form.account) return `${strings('select', 'account')} is required`
		if (!form.commodity?.commodity_id) return `${strings('select', 'commodity')} is required`
		if (!form.remark?.trim()) return `${strings('transaction', 'remark')} is required`
		if (!form.amount || Number(form.amount) <= 0) return `${strings('amount')} is required`
		if (attached > total) return 'Attached amount exceeds total amount'
		// Only validate the attached amount when invoices exist: at least one must be allocated
		// (attached > 0). With NO pending invoices it's a pure advance payment — skip this check.
		if (invoices.length > 0 && attached <= 0) return 'Amount should be more than 0'
		return null
	}

	const submit = () => {
		const error = validate()
		if (error) {
			dispatch(showToast(true, error, { type: TOAST_TYPE.ERROR }))
			return
		}

		// Use the account picked on the frontend; if the org already had an mbook_bank, fall back to it.
		const account = form.account ?? orgDetails?.mbook_bank ?? {}
		const payload = {
			payment_date: Number(form.paymentDate),
			amount: total,
			advance_amount: total,
			transaction_remarks: form.remark.trim(),
			commodity: {
				commodity_id: form.commodity?.commodity_id,
				commodity_name: form.commodity?.commodity_name,
			},
			// send the COMPLETE selected account object, ensuring the API's expected keys are present.
			// bank_name falls back to the account name when the account has no separate bank name.
			mbook_bank: {
				...account,
				account_id: account?.account_id || '',
				bank_name: account?.bank_name || account?.account_name || '',
				account_number: account?.account_number || '',
				ifsc_code: account?.ifsc_code || '',
			},
			user_details: {
				organisation_id: form.organisation?.organisation_id,
			},
			// logged-in user recording the payment
			sales_person: {
				user_id: user?.user_id,
				mobile_number: user?.mobile_number,
				full_name: user?.full_name,
				role: user?.role,
				organisation_id: user?.organisation_id,
				organisation_name: user?.organisation_name,
			},
			// Send the COMPLETE due-invoice object back, with the tagged amount applied on top.
			invoices: invoices
				.filter((inv) => Number(allocations[inv.id]) > 0)
				.map((inv) => ({
					...inv.raw,
					original_invoice_no: inv.number,
					amount: Number(allocations[inv.id]),
					// pending_amount from the due invoice (normalized into balanceDue).
					pending_amount: inv.balanceDue,
				})),
		}

		dispatch(showLoader(true))
		createInvoicePayment(payload, documents)
			.then(() => {
				dispatch(showToast(true, 'Payment recorded successfully', { type: TOAST_TYPE.SUCCESS }))
				update?.()
				close()
			})
			.catch((err) => {
				const msg = err?.response?.data?.message || err?.message || 'Failed to record payment'
				dispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<Wrapper>
			<FormWrapper>
				<SectionLabel>{strings('organisation')}</SectionLabel>
				<Select
					label={reqLabel(strings('select', 'organisation'))}
					placeholder={strings('select', 'organisation')}
					data={clients}
					displayKey='display_name'
					primaryKey='organisation_id'
					value={form.organisation ?? {}}
					onChange={onOrganisationChange}
				/>
				<Select
					label={reqLabel(strings('select', 'account'))}
					placeholder={strings('select account')}
					data={accounts}
					displayKey='display_label'
					primaryKey='account_number'
					value={accounts.find((item) => item?.account_number === form?.account?.account_number) ?? {}}
					onChange={(acc) => setField('account', acc)}
					disabled={!form.organisation}
				/>
				<Select
					label={reqLabel(strings('commodity'))}
					placeholder={strings('select', 'commodity')}
					data={commodities}
					displayKey='commodity_name'
					primaryKey='commodity_id'
					value={commodities.find((item) => item?.commodity_id === form?.commodity?.commodity_id) ?? form?.commodity ?? {}}
					onChange={(item) => setField('commodity', item)}
				/>

				<SectionLabel>{strings('payment', 'details')}</SectionLabel>
				<Input
					label={reqLabel(strings('transaction', 'remark'))}
					placeholder={strings('enter transaction remark')}
					value={form.remark}
					onChange={(e) => setField('remark', e.target.value)}
				/>
				<Input
					type='number'
					min='0'
					label={reqLabel(strings('amount'))}
					placeholder={strings('enter payment amount')}
					value={form.amount}
					onKeyDown={allowNumericKeyDown}
					onChange={(e) => handleAmountChange(e.target.value)}
				/>
				<Calendar
					label={strings('payment', 'date')}
					value={form.paymentDate}
					onChange={(val) => setField('paymentDate', val)}
					maxDate={new Date()}
					disableFutureMonth
				/>

				<SectionLabel>{strings('upload', 'documents')}</SectionLabel>
				<UploadButton files={documents} onChange={setDocuments} />

				{form.organisation && (
					<AttachInvoices
						invoices={invoices}
						allocations={allocations}
						allocErrors={allocErrors}
						attached={attached}
						advance={advance}
						onAllocationChange={handleAllocation}
					/>
				)}
			</FormWrapper>

			<SummaryBar>
				<SummaryItem>
					<SummaryLabel>{strings('total')}</SummaryLabel>
					<SummaryValue>{displayAmount(total)}</SummaryValue>
				</SummaryItem>
				<SummaryItem>
					<SummaryLabel>Attached</SummaryLabel>
					<SummaryValue>{displayAmount(attached)}</SummaryValue>
				</SummaryItem>
				<SummaryItem>
					<SummaryLabel>{strings('advance')}</SummaryLabel>
					<SummaryValue $advance>{displayAmount(advance)}</SummaryValue>
				</SummaryItem>
			</SummaryBar>

			<Footer>
				<Button variant='outline' color='neutral' onClick={close}>
					{strings('cancel')}
				</Button>
				<Button color='primary' onClick={submit}>
					{strings('submit', 'payment')}
				</Button>
			</Footer>
		</Wrapper>
	)
}

export default AddPayment
